import { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import {
  Receipt, Send, Bell, CheckCircle, Clock, AlertTriangle, 
  DollarSign, Search, Filter, Eye, Edit, Trash2, RefreshCw,
  FileText, Mail, Calendar, User, Building, Plus, Loader2, MapPin, Phone
} from 'lucide-react';
import InvoiceView from './InvoiceView';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function InvoiceManagement() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [editForm, setEditForm] = useState({
    notes: '',
    internal_notes: '',
    payment_terms: ''
  });
  
  // Create Invoice states
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [showInvoiceView, setShowInvoiceView] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerProperties, setCustomerProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [newInvoiceData, setNewInvoiceData] = useState(null);

  useEffect(() => {
    fetchInvoices();
    fetchCustomers();
  }, []);

  const fetchInvoices = async () => {
    try {
      const res = await axiosInstance.get('/invoices');
      // Calculate days overdue for each invoice
      const now = new Date();
      const processedInvoices = res.data.map(inv => {
        const dueDate = new Date(inv.due_date);
        const daysOverdue = Math.floor((now - dueDate) / (1000 * 60 * 60 * 24));
        return {
          ...inv,
          days_overdue: daysOverdue
        };
      });
      setInvoices(processedInvoices);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axiosInstance.get('/customers');
      setCustomers(res.data || []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const handleCustomerSelect = async (customerId) => {
    setSelectedCustomerId(customerId);
    setSelectedPropertyId('');
    
    if (!customerId) {
      setSelectedCustomer(null);
      setCustomerProperties([]);
      return;
    }
    
    try {
      const [customerRes, propertiesRes] = await Promise.all([
        axiosInstance.get(`/customers/${customerId}`),
        axiosInstance.get(`/customers/${customerId}/properties`)
      ]);
      setSelectedCustomer(customerRes.data);
      setCustomerProperties(propertiesRes.data || []);
      if (propertiesRes.data?.length > 0) {
        const primary = propertiesRes.data.find(p => p.is_primary) || propertiesRes.data[0];
        setSelectedPropertyId(primary.id);
      }
    } catch (error) {
      console.error('Failed to fetch customer details:', error);
      setSelectedCustomer(null);
      setCustomerProperties([]);
    }
  };

  const handleCreateNewInvoice = async () => {
    if (!selectedCustomerId) {
      toast.error('Please select a customer');
      return;
    }
    
    setCreatingInvoice(true);
    try {
      // Create a blank invoice for the selected customer
      const property = customerProperties.find(p => p.id === selectedPropertyId);
      const invoiceData = {
        customer_id: selectedCustomerId,
        customer_name: selectedCustomer?.name,
        customer_email: selectedCustomer?.email,
        customer_phone: selectedCustomer?.phone,
        customer_address: property?.full_address || selectedCustomer?.address || '',
        items: [],
        subtotal: 0,
        tax: 0,
        discount: 0,
        total_amount: 0,
        balance_due: 0,
        payment_terms: 'net_30',
        notes: ''
      };
      
      const res = await axiosInstance.post('/invoices', invoiceData);
      setNewInvoiceData(res.data);
      setShowCreateInvoice(false);
      setShowInvoiceView(true);
      toast.success(`Invoice ${res.data.invoice_number} created`);
      
      // Reset form
      setSelectedCustomerId('');
      setSelectedCustomer(null);
      setCustomerProperties([]);
      setSelectedPropertyId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await axiosInstance.post(`/invoices/${invoiceId}/send`);
      toast.success('Invoice sent to customer');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to send invoice');
    }
  };

  const handleDeleteInvoice = async () => {
    if (!selectedInvoice) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/invoices/${selectedInvoice.id}`);
      toast.success(`Invoice ${selectedInvoice.invoice_number} deleted`);
      setShowDeleteDialog(false);
      setSelectedInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    } finally {
      setDeleting(false);
    }
  };

  const handleSendReminder = async (invoiceId) => {
    try {
      const res = await axiosInstance.post(`/invoices/${invoiceId}/reminder`);
      toast.success(`Reminder sent (${res.data.reminder_count} total)`);
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedInvoice) return;
    try {
      const amount = paymentAmount ? parseFloat(paymentAmount) : null;
      const url = amount 
        ? `/invoices/${selectedInvoice.id}/pay?amount=${amount}`
        : `/invoices/${selectedInvoice.id}/pay`;
      
      const res = await axiosInstance.patch(url);
      toast.success(`Payment recorded. Balance due: $${res.data.balance_due.toFixed(2)}`);
      setShowPaymentDialog(false);
      setPaymentAmount('');
      setSelectedInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const handleUpdateInvoice = async () => {
    if (!selectedInvoice) return;
    try {
      await axiosInstance.patch(`/invoices/${selectedInvoice.id}`, editForm);
      toast.success('Invoice updated');
      setShowEditDialog(false);
      setSelectedInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const getStatusColor = (status, daysOverdue) => {
    if (status === 'paid') return 'bg-green-500';
    if (status === 'cancelled') return 'bg-gray-500';
    if (status === 'draft') return 'bg-slate-400';
    if (daysOverdue > 30) return 'bg-red-600';
    if (daysOverdue > 14) return 'bg-orange-500';
    if (status === 'sent' || status === 'payment_awaiting') return 'bg-blue-500';
    return 'bg-yellow-500';
  };

  const getStatusLabel = (status, daysOverdue) => {
    if (status === 'paid') return 'Paid';
    if (status === 'cancelled') return 'Cancelled';
    if (status === 'partially_paid') return 'Partial';
    if (status === 'draft') return 'Draft';
    if (daysOverdue > 30) return `Overdue ${daysOverdue}d`;
    if (daysOverdue > 14) return `Overdue ${daysOverdue}d`;
    if (daysOverdue > 0) return `Due ${daysOverdue}d ago`;
    if (status === 'sent') return 'Sent';
    return status?.toUpperCase() || 'PENDING';
  };

  const filteredInvoices = useMemo(() => {
    let filtered = invoices;

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(inv => 
        inv.invoice_number?.toLowerCase().includes(term) ||
        inv.customer_name?.toLowerCase().includes(term) ||
        inv.customer_email?.toLowerCase().includes(term)
      );
    }

    // Filter by tab
    switch (activeTab) {
      case 'draft':
        filtered = filtered.filter(inv => inv.status === 'draft');
        break;
      case 'sent':
        filtered = filtered.filter(inv => inv.status === 'sent' && inv.days_overdue <= 0);
        break;
      case 'awaiting':
        filtered = filtered.filter(inv => 
          ['sent', 'payment_awaiting', 'partially_paid'].includes(inv.status) && 
          inv.days_overdue <= 14
        );
        break;
      case 'paid':
        filtered = filtered.filter(inv => inv.status === 'paid');
        break;
      case 'overdue_14':
        filtered = filtered.filter(inv => 
          inv.status !== 'paid' && inv.status !== 'cancelled' && 
          inv.days_overdue > 14 && inv.days_overdue <= 30
        );
        break;
      case 'overdue_30':
        filtered = filtered.filter(inv => 
          inv.status !== 'paid' && inv.status !== 'cancelled' && 
          inv.days_overdue > 30
        );
        break;
      default:
        break;
    }

    return filtered.sort((a, b) => new Date(b.issue_date) - new Date(a.issue_date));
  }, [invoices, searchTerm, activeTab]);

  const stats = useMemo(() => {
    const paid = invoices.filter(i => i.status === 'paid');
    const pending = invoices.filter(i => ['sent', 'payment_awaiting', 'draft', 'partially_paid'].includes(i.status));
    const overdue14 = invoices.filter(i => i.status !== 'paid' && i.status !== 'cancelled' && i.days_overdue > 14 && i.days_overdue <= 30);
    const overdue30 = invoices.filter(i => i.status !== 'paid' && i.status !== 'cancelled' && i.days_overdue > 30);

    return {
      total: invoices.length,
      totalAmount: invoices.reduce((sum, i) => sum + (i.total_amount || 0), 0),
      paidCount: paid.length,
      paidAmount: paid.reduce((sum, i) => sum + (i.total_amount || 0), 0),
      pendingCount: pending.length,
      pendingAmount: pending.reduce((sum, i) => sum + ((i.balance_due || i.total_amount) || 0), 0),
      overdue14Count: overdue14.length,
      overdue14Amount: overdue14.reduce((sum, i) => sum + ((i.balance_due || i.total_amount) || 0), 0),
      overdue30Count: overdue30.length,
      overdue30Amount: overdue30.reduce((sum, i) => sum + ((i.balance_due || i.total_amount) || 0), 0)
    };
  }, [invoices]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Use imported formatDate from dateUtils directly - no local wrapper needed

  return (
    <div data-testid="invoice-management">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Invoice Management
          </h2>
          <p className="text-gray-600">Manage all invoices, track payments and send reminders</p>
        </div>
        <Button 
          className="bg-[#595959] hover:bg-[#404040]"
          onClick={() => setShowCreateInvoice(true)}
          data-testid="create-invoice-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Invoice
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-slate-500 to-slate-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-sm opacity-80">Total Invoices</div>
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm opacity-80">{formatCurrency(stats.totalAmount)}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-sm opacity-80">Paid</div>
            <div className="text-2xl font-bold">{stats.paidCount}</div>
            <div className="text-sm opacity-80">{formatCurrency(stats.paidAmount)}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-sm opacity-80">Awaiting Payment</div>
            <div className="text-2xl font-bold">{stats.pendingCount}</div>
            <div className="text-sm opacity-80">{formatCurrency(stats.pendingAmount)}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-sm opacity-80">Overdue &gt;14 Days</div>
            <div className="text-2xl font-bold">{stats.overdue14Count}</div>
            <div className="text-sm opacity-80">{formatCurrency(stats.overdue14Amount)}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white border-0">
          <CardContent className="p-4">
            <div className="text-sm opacity-80">Overdue &gt;30 Days</div>
            <div className="text-2xl font-bold">{stats.overdue30Count}</div>
            <div className="text-sm opacity-80">{formatCurrency(stats.overdue30Amount)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Search by invoice #, customer name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" onClick={fetchInvoices}>
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="grid grid-cols-7 w-full max-w-4xl">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="draft">Draft</TabsTrigger>
          <TabsTrigger value="sent">Sent</TabsTrigger>
          <TabsTrigger value="awaiting">Awaiting</TabsTrigger>
          <TabsTrigger value="paid">Paid</TabsTrigger>
          <TabsTrigger value="overdue_14">&gt;14 Days</TabsTrigger>
          <TabsTrigger value="overdue_30">&gt;30 Days</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Invoice Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#595959]"></div>
        </div>
      ) : (
        <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Issue Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.map((invoice) => (
                  <TableRow key={invoice.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{invoice.customer_name}</p>
                        <p className="text-xs text-gray-500">{invoice.customer_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {invoice.invoice_type?.toUpperCase() || 'FULL'}
                        {invoice.advance_percentage && ` (${invoice.advance_percentage}%)`}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(invoice.issue_date)}</TableCell>
                    <TableCell>{formatDate(invoice.due_date)}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(invoice.total_amount)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(invoice.balance_due || invoice.total_amount)}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getStatusColor(invoice.status, invoice.days_overdue)} text-white`}>
                        {getStatusLabel(invoice.status, invoice.days_overdue)}
                      </Badge>
                      {invoice.reminder_count > 0 && (
                        <Badge variant="outline" className="ml-1 text-xs">
                          {invoice.reminder_count} reminder{invoice.reminder_count > 1 ? 's' : ''}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setShowDetailDialog(true);
                          }}
                          title="View Details"
                        >
                          <Eye size={14} />
                        </Button>
                        
                        {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleSendInvoice(invoice.id)}
                              title={invoice.status === 'draft' ? 'Send Invoice' : 'Resend Invoice'}
                            >
                              <Send size={14} />
                            </Button>
                            
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedInvoice(invoice);
                                setPaymentAmount('');
                                setShowPaymentDialog(true);
                              }}
                              title="Record Payment"
                            >
                              <DollarSign size={14} />
                            </Button>
                            
                            {invoice.days_overdue > 0 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-orange-500"
                                onClick={() => handleSendReminder(invoice.id)}
                                title="Send Reminder"
                              >
                                <Bell size={14} />
                              </Button>
                            )}
                          </>
                        )}
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setEditForm({
                              notes: invoice.notes || '',
                              internal_notes: invoice.internal_notes || '',
                              payment_terms: invoice.payment_terms || ''
                            });
                            setShowEditDialog(true);
                          }}
                          title="Edit Invoice"
                        >
                          <Edit size={14} />
                        </Button>
                        
                        {/* Delete button - only for draft invoices */}
                        {invoice.status === 'draft' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setShowDeleteDialog(true);
                            }}
                            title="Delete Draft Invoice"
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {filteredInvoices.length === 0 && (
              <div className="text-center py-12">
                <Receipt size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No invoices found</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invoice Detail Dialog - Using Professional InvoiceView */}
      <InvoiceView
        open={showDetailDialog}
        onOpenChange={setShowDetailDialog}
        invoiceId={selectedInvoice?.id}
        invoiceData={selectedInvoice}
        onRefresh={fetchInvoices}
      />

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Invoice: {selectedInvoice?.invoice_number} - Balance: {formatCurrency(selectedInvoice?.balance_due || selectedInvoice?.total_amount)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Payment Amount (leave empty for full payment)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder={`Full amount: ${selectedInvoice?.balance_due || selectedInvoice?.total_amount}`}
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
              />
            </div>
            <Button className="w-full" onClick={handleMarkPaid}>
              <CheckCircle size={16} className="mr-2" />
              Record Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Invoice</DialogTitle>
            <DialogDescription>
              Update invoice {selectedInvoice?.invoice_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Payment Terms</Label>
              <Input
                value={editForm.payment_terms}
                onChange={(e) => setEditForm({ ...editForm, payment_terms: e.target.value })}
                placeholder="e.g., Due in 30 days"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (visible to customer)</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Additional notes..."
              />
            </div>
            <div className="space-y-2">
              <Label>Internal Notes (admin only)</Label>
              <Textarea
                value={editForm.internal_notes}
                onChange={(e) => setEditForm({ ...editForm, internal_notes: e.target.value })}
                placeholder="Internal notes..."
              />
            </div>
            <Button className="w-full" onClick={handleUpdateInvoice}>
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create New Invoice Dialog */}
      <Dialog open={showCreateInvoice} onOpenChange={setShowCreateInvoice}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#595959]" />
              Create New Invoice
            </DialogTitle>
            <DialogDescription>
              Select a customer to create a new invoice
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Customer Selection */}
            <div className="space-y-2">
              <Label>Select Customer *</Label>
              <Select value={selectedCustomerId} onValueChange={handleCustomerSelect}>
                <SelectTrigger data-testid="customer-select">
                  <SelectValue placeholder="Choose a customer..." />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {customer.name} - {customer.email}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Customer Details Preview */}
            {selectedCustomer && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-gray-900 flex items-center gap-2">
                  <User className="w-4 h-4 text-[#595959]" />
                  Customer Details
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Name</p>
                    <p className="font-medium">{selectedCustomer.name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Email</p>
                    <p className="font-medium">{selectedCustomer.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</p>
                    <p className="font-medium">{selectedCustomer.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Address</p>
                    <p className="font-medium">{selectedCustomer.address || 'N/A'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Property Selection */}
            {customerProperties.length > 0 && (
              <div className="space-y-2">
                <Label>Select Property / Service Address</Label>
                <Select value={selectedPropertyId} onValueChange={setSelectedPropertyId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a property..." />
                  </SelectTrigger>
                  <SelectContent>
                    {customerProperties.map((property) => (
                      <SelectItem key={property.id} value={property.id}>
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4" />
                          {property.property_name || 'Property'} - {property.full_address}
                          {property.is_primary && <Badge variant="outline" className="ml-2 text-xs">Primary</Badge>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setShowCreateInvoice(false)} className="flex-1">
              Cancel
            </Button>
            <Button 
              className="flex-1 bg-[#595959] hover:bg-[#404040]"
              onClick={handleCreateNewInvoice}
              disabled={!selectedCustomerId || creatingInvoice}
              data-testid="confirm-create-invoice"
            >
              {creatingInvoice ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Receipt className="w-4 h-4 mr-2" />
                  Create Invoice
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Professional Invoice View for New Invoice */}
      <InvoiceView
        open={showInvoiceView}
        onOpenChange={(open) => {
          setShowInvoiceView(open);
          if (!open) {
            setNewInvoiceData(null);
            fetchInvoices();
          }
        }}
        invoiceId={newInvoiceData?.id}
        invoiceData={newInvoiceData}
        onRefresh={fetchInvoices}
        editable={true}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 size={20} />
              Delete Draft Invoice
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this draft invoice? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="p-4 bg-gray-50 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Invoice #:</span>
                <span className="font-medium">{selectedInvoice.invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Customer:</span>
                <span className="font-medium">{selectedInvoice.customer_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Amount:</span>
                <span className="font-medium">{formatCurrency(selectedInvoice.total_amount)}</span>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteInvoice}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={14} className="mr-2" />
                  Delete Invoice
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
